import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  BarChart3,
  Brain,
  CreditCard,
  Eraser,
  FileSpreadsheet,
  Folder,
  Home,
  LogOut,
  Menu,
  Settings as SettingsIcon,
  Users,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { TrialBanner } from "@/components/subscription/TrialBanner";
import { useIsMobile } from "@/hooks/use-mobile";

const mainNav = [{ title: "Dashboard", icon: Home, url: "/" }];

const managementNav = [
  { title: "Team Management", icon: Users, url: "/team-management" },
  { title: "Folder Management", icon: Folder, url: "/folder-management" },
  { title: "AI Workflows", icon: Brain, url: "/ai-workflows" },
];

const dataSuiteNav = [
  { title: "Data Collection", icon: FileSpreadsheet, url: "/data" },
  { title: "Data Cleaning", icon: Eraser, url: "/data-cleaning" },
  { title: "Predictive Models", icon: Brain, url: "/predictive-models" },
  { title: "Dashboards", icon: BarChart3, url: "/dashboards" },
];

const accountNav = [
  { title: "Pricing", icon: CreditCard, url: "/pricing" },
  { title: "Settings", icon: SettingsIcon, url: "/settings" },
];

type MainLayoutProps = {
  children: React.ReactNode;
};

const MainLayout = ({ children }: MainLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [trialInfo, setTrialInfo] = useState<{
    isInTrial: boolean;
    trialEndDate: string | null;
    hasExpired: boolean;
  }>({
    isInTrial: false,
    trialEndDate: null,
    hasExpired: false
  });

  console.log("Current location:", location.pathname);

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success("Logged out successfully");
      // Redirect to external website
      window.location.href = "https://app.nextwaveai.space";
    } catch (error: any) {
      console.error("Error logging out:", error);
      toast.error(error.message || "Failed to log out");
    }
  };

  const renderMenuItem = (item: {
    title: string;
    icon: React.ElementType;
    url: string;
  }) => (
    <SidebarMenuItem key={item.title}>
      <SidebarMenuButton
        className={`w-full ${
          location.pathname === item.url ? "bg-primary/10 text-primary" : ""
        }`}
        onClick={() => navigate(item.url)}
      >
        <div className="flex items-center gap-3">
          <item.icon className="w-5 h-5" />
          <span>{item.title}</span>
        </div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  useEffect(() => {
    checkTrialStatus();
  }, []);

  const checkTrialStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      // Check if user has completed setup and is in trial
      const { data: payments } = await supabase
        .from('payments')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'successful')
        .eq('payment_type', 'one_time')
        .not('business_type', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1);

      if (payments && payments.length > 0) {
        const payment = payments[0];
        const trialEndDate = payment.trial_end_date;
        
        if (trialEndDate) {
          const now = new Date();
          const endDate = new Date(trialEndDate);
          const hasExpired = now > endDate;
          
          // Check if user has active subscription
          const { data: subscriptionPayments } = await supabase
            .from('payments')
            .select('*')
            .eq('user_id', user.id)
            .eq('payment_type', 'subscription')
            .eq('status', 'successful')
            .eq('subscription_status', 'active');

          const hasActiveSubscription = subscriptionPayments && subscriptionPayments.length > 0;

          setTrialInfo({
            isInTrial: !hasActiveSubscription,
            trialEndDate,
            hasExpired: hasExpired && !hasActiveSubscription
          });

          // If trial expired and no active subscription, redirect to pricing
          if (hasExpired && !hasActiveSubscription && location.pathname !== '/pricing') {
            navigate('/pricing');
          }
        }
      }
    } catch (error) {
      console.error("Error checking trial status:", error);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <Sidebar className="border-r" collapsible="icon">
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Main</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{mainNav.map(renderMenuItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Management</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{managementNav.map(renderMenuItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Data Suite</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{dataSuiteNav.map(renderMenuItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Account</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {accountNav.map(renderMenuItem)}
                  {/* Logout button */}
                  <SidebarMenuItem>
                  <SidebarMenuButton
                    className="w-full text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20"
                    onClick={handleLogout}
                  >
                    <div className="flex items-center gap-3">
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>

        <main className="flex-1">
          {/* Mobile header with sidebar trigger */}
          {isMobile && (
            <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b bg-background px-4 lg:hidden">
              <SidebarTrigger />
              <h1 className="font-semibold">NextWave AI</h1>
            </header>
          )}
          
          <div className="p-6">
            {/* Show trial banner if user is in trial or trial has expired */}
            {(trialInfo.isInTrial || trialInfo.hasExpired) && trialInfo.trialEndDate && (
              <TrialBanner 
                trialEndDate={trialInfo.trialEndDate}
              />
            )}
            {children}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default MainLayout;
